import { Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useWishlist } from '@/hooks/useWishlist';
import { useLocation } from 'wouter';

export default function WishlistPage() {
  const { wishlistItems, removeFromWishlist } = useWishlist();
  const [_, navigate] = useLocation();

  if (wishlistItems.length === 0) {
    return (
      <div className="min-h-screen bg-transparent py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/10 backdrop-blur-sm mb-6">
            <Heart className="h-10 w-10 text-primary/40" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4 font-heading">Your wishlist is empty</h2>
          <p className="text-lg text-gray-600 mb-8 max-w-md mx-auto">
            Start adding tours to your wishlist by clicking the "Add to Wishlist" button on any tour page.
          </p>
          <Button
            onClick={() => navigate('/destinations')}
            size="lg"
            className="rounded-full px-8 shadow-xl shadow-primary/20"
          >
            Explore Tours
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-10">
          <h1 className="text-4xl font-bold text-gray-900 font-heading">My Wishlist</h1>
          <p className="text-gray-500 mt-2">Saved safari experiences you want to explore later.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {wishlistItems.map((tour) => (
            <div
              key={tour.id}
              className="group bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden flex flex-col transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
            >
              <div className="relative h-56 overflow-hidden">
                <img
                  src={tour.image}
                  alt={tour.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2 font-heading">
                  {tour.name}
                </h3>
                {tour.description && (
                  <p className="text-gray-600 mb-5 line-clamp-3 text-sm leading-relaxed">
                    {tour.description}
                  </p>
                )}
                <div className="flex gap-3 mt-auto">
                  <Button
                    onClick={() => navigate(tour.href || `/tours/${tour.id}`)}
                    variant="outline"
                    className="flex-grow rounded-xl"
                  >
                    View Tour
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeFromWishlist(tour.id)}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50 rounded-xl"
                    title="Remove from wishlist"
                  >
                    <Heart className="h-5 w-5 fill-red-500" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}